import { useEffect, useState, useMemo } from 'react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { shallow } from '@jwp/ott-common/src/utils/compare';
import { getModule } from '@jwp/ott-common/src/modules/container';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';
import { useConfigStore } from '@jwp/ott-common/src/stores/ConfigStore';
import { useFavoritesStore } from '@jwp/ott-common/src/stores/FavoritesStore';
import FavoritesController from '@jwp/ott-common/src/controllers/FavoritesController';
import { useGADeviceType } from '@jwp/ott-common/src/utils/GADeviceType';
import { useAppConfig } from '@jwp/ott-hooks-react/src/useAppConfig';
import { useNavigationStack } from '@jwp/ott-common/src/stores/NavigationStackStore';
import { NetworkStatus } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { GET_FAVOURITES_LIST, GetFavouritesListData } from '@jwp/ott-common/src/graphql/query/favorite.queries';
import ApiService from '@jwp/ott-common/src/services/ApiService';
import type { PlaylistItem, Playlist } from '@jwp/ott-common/types/playlist';

import ConfirmationDialog from '../../components/ConfirmationDialog/ConfirmationDialog';
import Favorites from '../../components/Favorites/Favorites';
import LoadingOverlay from '../../components/LoadingOverlay/LoadingOverlay';
import Button from '../../components/Button/Button';
import useBreakpoint, { Breakpoint } from '../../hooks/useBreakpoint';

import styles from './FavouritesPage.module.scss';
import { sendGAEvent } from '@jwp/ott-hooks-react/src/useAnalytics';
import ErrorPage from '../../components/ErrorPage/ErrorPage';

const FavouritesPage = (): JSX.Element => {
  const favoritesController = getModule(FavoritesController);
  const apiService = getModule(ApiService);
  const [clearFavoritesOpen, setClearFavoritesOpen] = useState(false);
  const [playlist, setPlaylist] = useState<PlaylistItem[]>([]);
  const [playlistLoading, setPlaylistLoading] = useState(false);
  const [loadedMediaIdsKey, setLoadedMediaIdsKey] = useState('');

  const { accessModel, favoritesListId } = useConfigStore(
    (s) => ({
      accessModel: s.accessModel,
      favoritesListId: s.config.features?.favoritesList,
    }),
    shallow,
  );
  const navigate = useNavigate();

  const { t } = useTranslation(['error', 'user', 'common']);
  const breakpoint = useBreakpoint();

  const { user: customer, subscription, loading: accountLoading } = useAccountStore();
  const { isAnalyticsEnable, isCognitoEnabled, isKatonCognitoEnabled } = useAppConfig();
  const isCognitoMode = isCognitoEnabled || isKatonCognitoEnabled;
  const isLoggedIn = !!customer;
  const userId = isLoggedIn ? customer.id : 'Guest user';
  const deviceType = useGADeviceType();

  // Local/Store logic
  const favoritesFromStore = useFavoritesStore((state) => state.getPlaylist());
  const showFavourites = useFavoritesStore((state) => state.showFavourites);

  // Cognito/GraphQL logic
  const { data, loading: favoritesLoading, networkStatus, refetch, fetchMore } = useQuery<GetFavouritesListData>(GET_FAVOURITES_LIST, {
    variables: { first: 25, offset: 0 },
    skip: !isLoggedIn || !isCognitoMode,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const isFavoritesQueryInFlight =
    isCognitoMode &&
    (favoritesLoading || networkStatus === NetworkStatus.refetch || networkStatus === NetworkStatus.loading);

  const [loadingMore, setLoadingMore] = useState(false);
  const totalCount = data?.getFavouritesList?.totalCount || 0;
  const hasNextPage = (data?.getFavouritesList?.nodes?.length || 0) < totalCount;

  const handleLoadMore = async () => {
    if (loadingMore || !hasNextPage) { return; }
    setLoadingMore(true);
    try {
      await fetchMore({
        variables: {
          offset: data?.getFavouritesList?.nodes?.length || 0,
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) { return prev; }
          return {
            getFavouritesList: {
              ...fetchMoreResult.getFavouritesList,
              nodes: [
                ...(prev.getFavouritesList.nodes || []),
                ...(fetchMoreResult.getFavouritesList.nodes || []),
              ],
            },
          };
        },
      });
    } catch (error) {
      console.error('Error fetching more favorites:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  const { setAncestor, reset } = useNavigationStack();

  useEffect(() => {
    reset();
    setAncestor(`${window.location.pathname}${window.location.search}`);
  }, [reset, setAncestor]);

  const favoriteMediaIds = useMemo(() => {
    return data?.getFavouritesList?.nodes?.map((node) => node?.fJwMediaId).filter(Boolean) || [];
  }, [data]);

  const favoriteMediaIdsKey = favoriteMediaIds.join(',');

  useEffect(() => {
    let cancelled = false;

    const loadFavoritesPlaylist = async () => {
      if (!isCognitoMode || isFavoritesQueryInFlight || !data?.getFavouritesList) {
        return;
      }

      if (favoriteMediaIds.length === 0) {
        setPlaylist([]);
        setPlaylistLoading(false);
        setLoadedMediaIdsKey(favoriteMediaIdsKey);
        return;
      }

      if (!favoritesListId) {
        setPlaylistLoading(false);
        setLoadedMediaIdsKey(favoriteMediaIdsKey);
        return;
      }

      if (!loadingMore) {
        setPlaylistLoading(true);
      }

      try {
        const mediaItems = await apiService.getMediaByWatchlist(favoritesListId, favoriteMediaIds);
        if (cancelled) {
          return;
        }

        setPlaylist(mediaItems || []);
        setLoadedMediaIdsKey(favoriteMediaIdsKey);
      } catch (error) {
        console.error('Failed to fetch favorite media details', error);
        if (!cancelled) {
          setPlaylist([]);
          setLoadedMediaIdsKey(favoriteMediaIdsKey);
        }
      } finally {
        if (!cancelled && !loadingMore) {
          setPlaylistLoading(false);
        }
      }
    };

    void loadFavoritesPlaylist();

    return () => {
      cancelled = true;
    };
  }, [
    apiService,
    data?.getFavouritesList,
    favoriteMediaIds,
    favoriteMediaIdsKey,
    favoritesListId,
    isCognitoMode,
    isFavoritesQueryInFlight,
    loadingMore,
  ]);

  useEffect(() => {
    if (isAnalyticsEnable) {
      sendGAEvent('screen_view', {
        firebase_screen_class: 'Favourites',
        firebase_screen: 'Favourites',
        device_type: deviceType,
        userid_no: userId,
      });
    }
  }, []);

  useEffect(() => {
    if (!isAnalyticsEnable) {
      return;
    }

    let lastScrollTop = 0;

    const handleScroll = _.throttle(() => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const direction = scrollTop > lastScrollTop ? 'Down' : 'Up';
      lastScrollTop = scrollTop <= 0 ? 0 : scrollTop;

      if (scrollTop > 0) {
        sendGAEvent('scroll', {
          scroll_direction: direction,
          userid_no: userId,
          device_type: deviceType,
        });
      }
    }, 1000);

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (!accountLoading && !customer) {
      navigate('/', { replace: true });
    }
  }, [navigate, customer, accountLoading]);

  const currentPlaylist = useMemo(() => {
    if (isCognitoMode) {
      return {
        kind: 'WATCHLIST',
        playlist,
        title: 'Favorites',
        feedid: favoritesListId,
      } as unknown as Playlist;
    }
    return favoritesFromStore;
  }, [isCognitoMode, playlist, favoritesFromStore, favoritesListId]);

  const isAwaitingPlaylist =
    isCognitoMode &&
    favoriteMediaIds.length > 0 &&
    loadedMediaIdsKey !== favoriteMediaIdsKey &&
    !loadingMore;

  const isPageLoading =
    accountLoading ||
    (isCognitoMode && isFavoritesQueryInFlight && playlist.length === 0) ||
    (isCognitoMode && playlistLoading && !loadingMore) ||
    isAwaitingPlaylist;

  if (isPageLoading) {
    return (
      <div className={styles.user}>
        <LoadingOverlay inline />
      </div>
    );
  }

  if (!showFavourites) {
    return <ErrorPage title={t('empty_shelves_heading')} message={t('empty_shelves_description')} alignTop={false} />;
  }

  return (
    <div className={styles.favouriteContainer}>
      <Favorites
        playlist={currentPlaylist}
        onClearFavoritesClick={() => setClearFavoritesOpen(true)}
        accessModel={accessModel}
        hasSubscription={!!subscription}
      />
      {hasNextPage && isCognitoMode && (
        <div className={styles.loadMoreContainer}>
          <Button
            label={loadingMore ? t('user:loading') : t('common:load_more')}
            aria-label={t('common:load_more')}
            onClick={handleLoadMore}
            disabled={loadingMore}
            color="default"
            fullWidth={breakpoint < Breakpoint.md}
            size="large"
          />
        </div>
      )}
      <ConfirmationDialog
        open={clearFavoritesOpen}
        title={t('user:favorites.clear_favorites_title')}
        body={t('user:favorites.clear_favorites_body')}
        onConfirm={async () => {
          await favoritesController.clear();
          setClearFavoritesOpen(false);
          if (isCognitoMode) {
            refetch();
          }
        }}
        onClose={() => setClearFavoritesOpen(false)}
      />
    </div>
  );
};

export default FavouritesPage;
